// Ionic & Angular Imports
import {Component} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, Popover, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsSubcontractorSvc, Subcontractor} from '../../../../../services/subcontractor.service';
import {Company} from '../../../../../services/company.service';

// Component Imports
import {BasePage} from '../../../../base.component';
import {SubcontractorPopover} from '../subcontractor-popover/subcontractor-popover.component';

@Component({
    templateUrl: 'subcontractor-modal.html',
    providers: [
        rsSubcontractorSvc
    ]
})
export class SubcontractorModal extends BasePage
{
    // Class member variables
    private modalForm      : FormGroup;
    private editMode       : boolean;
    private initSub        : Subcontractor;
    private companyList    : Company[];

    constructor( 
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private subSvc: rsSubcontractorSvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        this.companyList = this.navParams.get("companies");
        if( this.editMode ) { 
            this.initSub = <Subcontractor>this.navParams.get("subcontractor");
        } else {
            // Initialize our activity object
            this.initSub = <Subcontractor>{
                id: 0,
                subcontractor : {
                    id: 0,
                    name: "",
                },
                crewsize: 1,
                starttime: "06:00",
                finishtime: "15:00",
                notes: ""
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        let name : string = "";
        if( this.initSub.subcontractor.id > 0 ) {
            name = this.initSub.subcontractor.name
        }
        this.modalForm = this.formBuilder.group({
            name:           [name, Validators.required],
            subid:          [this.initSub.subcontractor.id, Validators.required],
            crewsize:       [this.initSub.crewsize, Validators.required],   // TODO set up min/max validation
            starttime:      [this.initSub.starttime, Validators.required ],
            finishtime:     [this.initSub.finishtime, Validators.required ],
            notes:          [this.initSub.notes]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    public selectCompany(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( SubcontractorPopover, {
            companyList: this.companyList
        }, {
            cssClass: "customPopover"
        } );
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                let company : Company = data.selected;
                this.setFormField( 'subid', company.id );
                this.setFormField( 'name', company.name );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });    
    }    

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        let name : string = "";
        if( this.initSub.subcontractor.id > 0 ) {
            name = this.initSub.subcontractor.name;
        }        
        this.modalForm.reset( {
            name:           name,
            subid:          this.initSub.subcontractor.id,
            crewsize:       this.initSub.crewsize,
            starttime:      this.initSub.starttime,  
            finishtime:     this.initSub.finishtime,
            notes:          this.initSub.notes              
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button should be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new subcontractor
                this.subSvc.addSubcontractor( formData.subid, formData.crewsize, formData.starttime, formData.finishtime, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('subcontractor:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this subcontractor to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing subcontractor
                this.subSvc.updateSubcontractor( this.initSub.id, formData.crewsize, formData.subid, formData.starttime, formData.finishtime, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('subcontractor:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this subcontractor.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initSub.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Subcontractor', 'Are you sure you want to delete "' + this.initSub.subcontractor.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.subSvc.deleteSubcontractor( this.initSub.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('subcontractor:deleted', this.initSub );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this subcontractor.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }    
}    


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/subcontractor/subcontractor-modal/subcontractor-modal.component.ts